/**
 * CodeDetailsModal — purpose-built drawer for the Codes tab.
 *
 * Separate from ``CodeQuotesModal`` (which lives next to the Codebook
 * tab) because that one is entangled with the codebook's edit/delete
 * flow, a transcript-detail fetch, and an early-return that swallows
 * the dialog before its data has loaded. For the Codes tab we just
 * want: name + definition + every place this code was applied,
 * grouped by item, with the quote text + a link to the source.
 *
 * Handles both source shapes from ``codes_quotes_dict``:
 *   transcript-style: ``{transcript_id, turn_id, text, context?}``
 *   document-style:   ``{document_id, paragraph_id, text, context?}``
 * Either way the user sees the surrounding context + a link that
 * jumps to the original item.
 */
import { useFetchCodeDetails } from '@hooks/getQueries';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

/**
 * Collapse the heterogenous source shape into a flat
 * ``{itemId, itemKind, quoteIdx, text, context}`` row so the JSX
 * doesn't need to branch on every field.
 *
 * ``itemKind`` drives the icon + verb in the row (a transcript turn vs
 * a document paragraph). When neither kind is present we still render
 * the quote — better than dropping a code occurrence.
 */
function flattenSources(rawEntry) {
  if (!rawEntry) return [];
  const sources = Array.isArray(rawEntry) ? rawEntry : rawEntry?.sources || [];
  return sources.map((src) => {
    const itemId = src.transcript_id || src.document_id || null;
    const itemKind = src.document_id ? 'document' : src.transcript_id ? 'transcript' : 'unknown';
    const quoteIdx =
      itemKind === 'document'
        ? (src.paragraph_id ?? null)
        : itemKind === 'transcript'
          ? (src.turn_id ?? null)
          : null;
    return {
      itemId,
      itemKind,
      quoteIdx,
      text: (src.text || '').trim(),
      context: src.context || null,
    };
  });
}

/**
 * Group flattened sources by item so the modal shows
 *   - Item A
 *       quote 1
 *       quote 2
 *   - Item B
 *       quote 1
 * rather than one flat list (which obscures cross-item patterns).
 */
function groupByItem(flat) {
  const groups = new Map();
  for (const row of flat) {
    const key = row.itemId || '__unknown__';
    if (!groups.has(key)) {
      groups.set(key, {
        itemId: row.itemId,
        itemKind: row.itemKind,
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  }
  return Array.from(groups.values());
}

const CodeDetailsModal = ({ open, onClose, code, codesQuotesDict }) => {
  const { id: studyId } = useParams();

  const flat = useMemo(
    () => (codesQuotesDict && code ? flattenSources(codesQuotesDict[code.name]) : []),
    [codesQuotesDict, code],
  );
  const groups = useMemo(() => groupByItem(flat), [flat]);

  // Pull the richer per-code record from the codes/details endpoint
  // (definition + LLM-generated summary). Only fires when the modal is
  // open and we have a name — avoids prefetching for every row in the
  // table. Definition falls back to what the caller passed when the
  // endpoint hasn't responded yet.
  const { data: codeDetails, isFetching: isFetchingDetails } = useFetchCodeDetails({
    studyId,
    codeName: open && code ? code.name : null,
  });

  // "See more / less" affordance for the summary. Long summaries used
  // to swallow most of the dialog and leave the quotes barely visible;
  // now the summary stays clamped to ~3 lines and the user expands it
  // when they want the full text.
  //
  // Trigger: character-length heuristic (~180 chars ≈ 3 lines at the
  // dialog's typical width). The previous implementation measured
  // scrollHeight vs clientHeight, but that races the Dialog's mount
  // transition on some entry points (row-click on the Codes tab, the
  // Treemap-drill-down modal) — the initial useLayoutEffect measure
  // fires while the dialog is still fading in and hasn't reached its
  // final width, so scrollHeight === clientHeight and the toggle
  // silently never appears. Length-based detection sidesteps all of
  // that: if the text is long enough that it'd overflow at any
  // reasonable width, we show the toggle. Short summaries still stay
  // clean.
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const summaryRef = useRef(null);
  const summary = codeDetails?.summary;
  const summaryOverflows = (summary || '').length > 180;

  useEffect(() => {
    // Reset expansion whenever the summary text changes (new code opened,
    // or the details endpoint resolves after the initial empty state).
    setSummaryExpanded(false);
  }, [summary]);

  if (!code) return null;

  const definition = codeDetails?.definition || code.definition;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {/* Header — keep it a plain Box so we don't trigger MUI's
          DialogTitle Typography wrapping (which renders an h2 around
          a variant-h6 child and produces the nested-h6 DOM warning). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          p: 3,
          pb: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {code.name}
            </Typography>
            <Chip
              size="small"
              label={`${flat.length} ${flat.length === 1 ? 'occurrence' : 'occurrences'}`}
            />
          </Stack>

          <Stack spacing={1}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                Definition
              </Typography>
              {definition ? (
                <Typography variant="body2" color="text.primary">
                  {definition}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.disabled" display="block">
                  (no definition)
                </Typography>
              )}
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                Summary
              </Typography>
              {summary ? (
                <>
                  <Typography
                    ref={summaryRef}
                    variant="body2"
                    color="text.primary"
                    sx={
                      summaryExpanded
                        ? undefined
                        : {
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }
                    }
                  >
                    {summary}
                  </Typography>
                  {summaryOverflows || summaryExpanded ? (
                    <MuiLink
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => setSummaryExpanded((v) => !v)}
                      underline="hover"
                      sx={{ mt: 0.5, fontWeight: 600 }}
                    >
                      {summaryExpanded ? 'See less' : 'See more'}
                    </MuiLink>
                  ) : null}
                </>
              ) : isFetchingDetails ? (
                <Skeleton variant="text" width="80%" />
              ) : (
                <Typography variant="caption" color="text.disabled" display="block">
                  (no summary yet — generated when the codebook step runs)
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {groups.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              This code wasn&rsquo;t applied to any item in this analysis.
            </Typography>
          </Box>
        ) : (
          <Box>
            {groups.map((group, gIdx) => {
              const isDoc = group.itemKind === 'document';
              const itemLink = group.itemId
                ? `/interview/${studyId}/transcripts/${group.itemId}`
                : null;
              return (
                <Box
                  key={group.itemId || `unknown-${gIdx}`}
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: gIdx < groups.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    {isDoc ? (
                      <ArticleOutlinedIcon fontSize="small" color="action" />
                    ) : (
                      <ForumOutlinedIcon fontSize="small" color="action" />
                    )}
                    <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
                      {isDoc ? 'Document' : 'Transcript'} ·{' '}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85em',
                          color: 'text.secondary',
                        }}
                      >
                        {group.itemId || 'unknown source'}
                      </Box>
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${group.rows.length} ${group.rows.length === 1 ? 'quote' : 'quotes'}`}
                    />
                    {itemLink ? (
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={itemLink}
                        onClick={onClose}
                        aria-label="Open item"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>

                  <Stack spacing={1}>
                    {group.rows.map((row, rIdx) => {
                      const ctx = row.context || {};
                      const beforeText =
                        ctx.before || ctx.question || ctx.previous_paragraph || null;
                      const afterText = ctx.after || ctx.response || null;
                      return (
                        <Box
                          key={`${row.quoteIdx ?? rIdx}-${rIdx}`}
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {row.quoteIdx !== null ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              {isDoc ? `¶ ${row.quoteIdx}` : `Turn ${row.quoteIdx}`}
                            </Typography>
                          ) : null}
                          {beforeText ? (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              display="block"
                              mb={0.5}
                              sx={{ fontStyle: 'italic' }}
                            >
                              {beforeText}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="body2"
                            sx={{
                              bgcolor: 'warning.lighter',
                              backgroundColor: '#fff7c2',
                              p: 0.5,
                              borderRadius: 0.5,
                              display: 'inline',
                            }}
                          >
                            {row.text || (
                              <Typography component="span" variant="caption" color="text.disabled">
                                (quote text missing)
                              </Typography>
                            )}
                          </Typography>
                          {afterText ? (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              display="block"
                              mt={0.5}
                              sx={{ fontStyle: 'italic' }}
                            >
                              {afterText}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>

                  {itemLink ? (
                    <Box mt={1.5}>
                      <MuiLink
                        component={RouterLink}
                        to={itemLink}
                        onClick={onClose}
                        underline="hover"
                        variant="caption"
                      >
                        Open {isDoc ? 'document' : 'transcript'} →
                      </MuiLink>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CodeDetailsModal;
